
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { ProfileCompletion } from "@/components/auth/ProfileCompletion";
import { useAuth } from "@/lib/context/auth-context";
import { Loader2 } from "lucide-react";
import { toast } from "@/components/ui/use-toast";

export default function Callback() {
  const [isLoading, setIsLoading] = useState(true);
  const [isNewUser, setIsNewUser] = useState(false);
  const navigate = useNavigate();
  const { user, profile } = useAuth();

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        console.log("Auth callback started");
        // Process the OAuth callback
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("Session error:", error);
          throw error;
        }
        
        if (!data.session) {
          console.error("No session found");
          throw new Error("No session found");
        }

        console.log("Session obtained:", !!data.session);
        console.log("User from session:", !!data.session.user);
        
        // Check if profile exists and is complete
        if (data.session.user) {
          console.log("Checking profile information...");
          
          const { data: profileData, error: profileError } = await supabase
            .from("profiles")
            .select("*")
            .eq("id", data.session.user.id)
            .single();
          
          if (profileError && profileError.code !== "PGRST116") {
            console.error("Profile fetch error:", profileError);
          }
          
          console.log("Profile data from direct fetch:", profileData);
          
          // Log profile completion status
          console.log("Profile completion check:", {
            hasProfile: !!profileData,
            schoolDistrictStatus: profileData?.school_district === null ? "null" : "has value",
            schoolStatus: profileData?.school === null ? "null" : "has value",
            positionStatus: profileData?.position === null ? "null" : "has value",
            isIncomplete: !profileData || 
                          profileData.school_district === null || 
                          profileData.school === null || 
                          profileData.position === null
          });
          
          // Determine if this is a new user or incomplete profile
          // Check explicitly for null or undefined values for required fields
          if (!profileData || 
              profileData.school_district === null || 
              profileData.school === null || 
              profileData.position === null) {
            console.log("Incomplete profile detected, showing completion form");
            setIsNewUser(true);
          } else {
            console.log("Complete profile found, redirecting to dashboard");
            toast({
              title: "Successfully signed in",
              description: "Welcome back!",
            });
            navigate("/");
          }
        }
        
        setIsLoading(false);
      } catch (error) {
        console.error("Error during auth callback:", error);
        setIsLoading(false);
        toast({
          variant: "destructive",
          title: "Authentication error",
          description: "There was a problem signing you in. Please try again.",
        });
        navigate("/auth/login");
      }
    };

    handleAuthCallback();
  }, [navigate]);

  // Show loading indicator while processing
  if (isLoading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-secondary/5 to-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-2 text-muted-foreground font-serif">Completing authentication...</p>
        </div>
      </div>
    );
  }

  // Show profile completion form for new users
  if (isNewUser && user) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-secondary/5 to-background">
        <div className="w-full max-w-md px-4">
          <ProfileCompletion userId={user.id} />
        </div>
      </div>
    );
  }

  // Fallback - should not normally reach here
  return (
    <div className="h-screen w-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-secondary/5 to-background">
      <div className="text-center">
        <p className="text-muted-foreground font-serif">Redirecting...</p>
      </div>
    </div>
  );
}
